import { Box, Button, Card, HStack, Input, Text } from '@chakra-ui/react'

import { Checkbox, Field } from '@mangovoice/mango-ui'

import type { PbxSettingsApi } from '../api/pbxSettingsApi.types'
import type { PbxSettingsTranslateFn } from '../types'
import { text } from '../types'
import type { PbxFormValues, SelectOption } from '../types/pbxSettings.types'
import { HeadingText, InputField, SelectField } from './PbxFormFields'
import { PbxSettingsCompanySelect } from './PbxSettingsCompanySelect'

interface PbxSettingsCoreSectionProps {
  api: PbxSettingsApi
  t?: PbxSettingsTranslateFn
  pbx: PbxFormValues
  selectedCompanyName?: string
  /** Company page name resolved for pbx.company_id from the companies list. */
  companyPageName?: string
  outboundProviderGatewayOptions: SelectOption[]
  emergencyProviderGatewayOptions: SelectOption[]
  internationalProviderGatewayOptions: SelectOption[]
  onPbxFieldChange: <K extends keyof PbxFormValues>(
    field: K,
    value: PbxFormValues[K],
  ) => void
  onResolvedCompany?: (
    company: {
      id: number
      name: string
    } | null,
  ) => void
}

export const PbxSettingsCoreSection = ({
  api,
  t,
  pbx,
  selectedCompanyName,
  companyPageName,
  onResolvedCompany,
  outboundProviderGatewayOptions,
  emergencyProviderGatewayOptions,
  internationalProviderGatewayOptions,
  onPbxFieldChange,
}: PbxSettingsCoreSectionProps) => (
  <Card.Root bg="bg.panel" rounded="xl">
    <Card.Header p={6} pb={2}>
      <HeadingText>
        {text(t, 'pbxSettings.sections.core.title', 'PBX Settings')}
      </HeadingText>
    </Card.Header>
    <Card.Body p={6} pt={2}>
      <Box display="grid" gridTemplateColumns="1fr" gap={4}>
        <InputField
          label={text(
            t,
            'pbxSettings.fields.pbxDisplayName.label',
            'PBX Display Name',
          )}
          required
          value={pbx.name}
          onChange={(value) => onPbxFieldChange('name', value)}
        />
        <Field.Root required>
          <Field.Label>
            {text(t, 'pbxSettings.fields.company.label', 'Company')}
          </Field.Label>
          <PbxSettingsCompanySelect
            api={api}
            t={t}
            selectedCompanyId={
              pbx.company_id ? Number.parseInt(pbx.company_id, 10) : undefined
            }
            selectedCompanyName={selectedCompanyName}
            onCompanyChange={(company) =>
              onPbxFieldChange('company_id', company ? String(company.id) : '')
            }
            onResolvedCompany={onResolvedCompany}
            placeholder={text(
              t,
              'pbxSettings.placeholders.selectFromList',
              'Select from the List...',
            )}
          />
        </Field.Root>
        <Field.Root required>
          <Field.Label>
            {text(
              t,
              'pbxSettings.fields.smsSenderName.label',
              'SMS Sender Name',
            )}
          </Field.Label>
          <HStack gap={2}>
            <Input
              value={pbx.company_name}
              onChange={(event) =>
                onPbxFieldChange('company_name', event.target.value)
              }
              bg="bg.subtle"
              borderColor="border.default"
              borderRadius="lg"
              _focusVisible={{
                borderColor: 'blue.500',
                boxShadow: '0 0 0 2px var(--chakra-colors-blue-500)',
              }}
            />
            {companyPageName && companyPageName !== pbx.company_name && (
              <Button
                variant="outline"
                size="sm"
                whiteSpace="nowrap"
                onClick={() =>
                  onPbxFieldChange('company_name', companyPageName)
                }
              >
                {text(
                  t,
                  'pbxSettings.fields.smsSenderName.copyFromCompany',
                  'Copy from Company',
                )}
              </Button>
            )}
          </HStack>
          <Text color="fg.muted" fontSize="sm">
            {text(
              t,
              'pbxSettings.fields.smsSenderName.helperText',
              'Shown to your customers in SMS opt-out replies, e.g. "{{name}}: You have successfully unsubscribed." This is per-location — edit it here if it differs from the company name.',
              {
                name:
                  pbx.company_name ||
                  text(
                    t,
                    'pbxSettings.fields.smsSenderName.label',
                    'SMS Sender Name',
                  ),
              },
            )}
          </Text>
          {companyPageName && companyPageName !== pbx.company_name && (
            <Text color="fg.muted" fontSize="sm">
              {text(
                t,
                'pbxSettings.fields.smsSenderName.divergenceHint',
                'Differs from company name "{{companyName}}" — this is expected for multi-location customers.',
                { companyName: companyPageName },
              )}
            </Text>
          )}
        </Field.Root>
        <SelectField
          t={t}
          label={text(
            t,
            'pbxSettings.fields.providerGateway.label',
            'Provider gateway',
          )}
          required
          value={pbx.provider_gateway_id}
          onChange={(value) => onPbxFieldChange('provider_gateway_id', value)}
          options={outboundProviderGatewayOptions}
          placeholder={text(
            t,
            'pbxSettings.placeholders.selectFromList',
            'Select from the List...',
          )}
        />
        <SelectField
          t={t}
          label={text(
            t,
            'pbxSettings.fields.emergencyProviderGateway.label',
            'Emergency provider gateway',
          )}
          required
          value={pbx.emergency_provider_gateway_id}
          onChange={(value) =>
            onPbxFieldChange('emergency_provider_gateway_id', value)
          }
          options={emergencyProviderGatewayOptions}
          placeholder={text(
            t,
            'pbxSettings.placeholders.selectFromList',
            'Select from the List...',
          )}
        />
        <SelectField
          t={t}
          label={text(
            t,
            'pbxSettings.fields.internationalProviderGateway.label',
            'International provider gateway',
          )}
          required
          value={pbx.international_provider_gateway_id}
          onChange={(value) =>
            onPbxFieldChange('international_provider_gateway_id', value)
          }
          options={internationalProviderGatewayOptions}
          placeholder={text(
            t,
            'pbxSettings.placeholders.selectFromList',
            'Select from the List...',
          )}
        />
        <Box display="flex" alignItems="center">
          <Checkbox
            gap={0}
            checked={pbx.active}
            onCheckedChange={(event) =>
              onPbxFieldChange('active', Boolean(event.checked))
            }
          >
            {text(t, 'pbxSettings.fields.active.label', 'Active')}
          </Checkbox>
        </Box>
      </Box>
    </Card.Body>
  </Card.Root>
)
